import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Spin, Table, Upload, message } from 'antd';
import { CheckCircleOutlined, InboxOutlined } from '@ant-design/icons';
import {
    useGetRuleSetsQuery,
    usePreviewRuleSetMutation,
    useConfirmRuleSetMutation,
} from '../../redux/api/ruleSets';

// Shared by the legacy route and the contextual client drawer. Keeping the
// preview and confirmation here ensures both paths record the same approval.
const DataCheckPanel = ({ clientName, initialFile, onApproved }) => {
    const { data: ruleSetData, isLoading: rulesLoading } = useGetRuleSetsQuery(
        { clientName }, { skip: !clientName });
    const [previewRuleSet, { isLoading: checking }] = usePreviewRuleSetMutation();
    const [confirmRuleSet, { isLoading: approving }] = useConfirmRuleSetMutation();
    const [result, setResult] = useState(null);
    const [approved, setApproved] = useState(false);
    const previewedFile = useRef(null);

    const target = useMemo(() => {
        const sets = ruleSetData?.rule_sets || [];
        if (!sets.length) return null;
        const rs = sets[sets.length - 1];
        const latest = rs.versions[rs.versions.length - 1];
        return { ruleId: rs.rule_id, ...latest };
    }, [ruleSetData]);

    const handleFile = async (file) => {
        if (!target) return false;
        setResult(null);
        setApproved(false);
        try {
            const res = await previewRuleSet({
                clientName, ruleId: target.ruleId, version: target.version, file,
            }).unwrap();
            setResult(res);
        } catch {
            message.error("We couldn't read that file. Check it's the usual CSV or Excel export and try again.");
        }
        return false;
    };

    useEffect(() => {
        if (initialFile && target && previewedFile.current !== initialFile) {
            previewedFile.current = initialFile;
            handleFile(initialFile);
        }
        // handleFile deliberately depends on the latest target.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFile, target]);

    const handleApprove = async () => {
        try {
            await confirmRuleSet({
                clientName, ruleId: target.ruleId, version: target.version,
            }).unwrap();
            setApproved(true);
            onApproved?.();
            message.success('Thanks — your approval has been recorded.');
        } catch {
            message.error("That didn't save. Please try again, or tell the Compass team.");
        }
    };

    const sampleColumns = useMemo(() => {
        const first = result?.sample?.[0];
        if (!first) return [];
        return Object.keys(first).slice(0, 12).map((key) => ({
            title: key, dataIndex: key, key, ellipsis: true,
        }));
    }, [result]);

    if (rulesLoading) return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;

    if (!target) {
        return <Alert type="info" showIcon message="Nothing for you to do here yet — the Compass team is setting this up." />;
    }

    return (
        <div>
            {!initialFile && !result && (
                <Upload.Dragger accept=".csv,.xlsx,.xls" showUploadList={false}
                    beforeUpload={handleFile} disabled={checking}>
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p className="ant-upload-text">Drop the file here to see how we'll organise it</p>
                </Upload.Dragger>
            )}
            {checking && <div style={{ textAlign: 'center', padding: 40 }}><Spin /> <span style={{ marginLeft: 12 }}>Checking your file…</span></div>}
            {result && !checking && (
                <div>
                    <Alert type="success" showIcon
                        message={`We could read your file — ${result.rows.toLocaleString()} rows found.`}
                        style={{ marginBottom: 16 }} />
                    {(result.validation_flags || []).length > 0 && (
                        <Alert type="warning" showIcon message="A few things we noticed"
                            description={<ul style={{ margin: 0, paddingLeft: 18 }}>{result.validation_flags.map((flag, i) => <li key={i}>{String(flag)}</li>)}</ul>}
                            style={{ marginBottom: 16 }} />
                    )}
                    <h3>Here's a sample of how your file will be organised</h3>
                    <Table size="small" columns={sampleColumns}
                        dataSource={(result.sample || []).map((row, i) => ({ key: i, ...row }))}
                        pagination={false} scroll={{ x: true }} />
                    <div style={{ marginTop: 24, padding: 16, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8 }}>
                        {result.status === 'confirmed' || approved ? (
                            <span><CheckCircleOutlined style={{ color: '#389e0d', marginRight: 8 }} />Approved — you're all set.</span>
                        ) : (
                            <>
                                <h3 style={{ marginTop: 0 }}>Does this look right?</h3>
                                <p>If the sample matches what you expect, approve it and we'll use this layout from now on.</p>
                                <Button type="primary" size="large" loading={approving} onClick={handleApprove}>Yes — this looks right</Button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataCheckPanel;
